import { toast } from 'sonner';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Send, XCircle, Calendar, Plus, Pencil } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import invoicesService from '../services/invoicesService';
import { exportRowsToCsv } from '../utils/csvExport';
import ListPager, { usePagedRows } from '../components/ListPager';
import { useTableSort, SortTh } from '../components/SortableTable';
import HelpLink from '../components/HelpLink';
import { formatDate, formatDateTime } from '../utils/datetime';

const STATUS_STYLES = {
  draft: 'bg-slate-50 text-slate-500',
  sent: 'bg-blue-50 text-blue-600',
  paid: 'bg-emerald-50 text-emerald-600',
  overdue: 'bg-red-50 text-red-500',
  void: 'bg-amber-50 text-amber-600',
};

const Invoices = () => {
  const { facilityId, currentFacility, loading: facilityLoading } = useFacility();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  // 'all' | 'open' (not paid/void) | 'paid' | 'void'
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  // Due-date window: preset pills ('all'|'today'|'week'|'month') with an
  // explicit from/to range overriding them — same pattern as Payments.
  const [dateFilter, setDateFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Invoice detail modal: the row's full breakdown + covering payments.
  const [detail, setDetail] = useState(null);
  const [detailPayments, setDetailPayments] = useState(null);

  const openDetail = async (inv) => {
    setDetail(inv);
    setDetailPayments(null);
    try {
      const res = await invoicesService.payments(facilityId, inv._id);
      setDetailPayments(res.data.payments || []);
    } catch {
      setDetailPayments([]);
    }
  };

  const fetchInvoices = useCallback(async () => {
    if (!facilityId) {
      if (!facilityLoading) setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Default view is the last 90 days, newest first. But the date-range
      // picker and the "past 7 days"/"this month" chips reach OLDER invoices
      // — if the fetch stayed capped at 90 days, those filters would silently
      // find nothing beyond the window. So when an explicit range is set,
      // fetch the full history and let the client-side filter narrow it.
      const usingExplicitRange = Boolean(fromDate || toDate)
        || (dateFilter !== 'all' && dateFilter !== 'today');
      const res = await invoicesService.getAll(
        facilityId, usingExplicitRange ? null : 90);
      setInvoices(res.data);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    } finally {
      setLoading(false);
    }
  }, [facilityId, facilityLoading, fromDate, toDate, dateFilter]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleSend = async (id) => {
    try {
      await invoicesService.send(facilityId, id);
      fetchInvoices();
    } catch (err) {
      console.error('Failed to send invoice:', err);
    }
  };

  const handleVoid = async (id) => {
    if (!window.confirm('Void this invoice?')) return;
    try {
      await invoicesService.void(facilityId, id);
      fetchInvoices();
    } catch (err) {
      console.error('Failed to void invoice:', err);
    }
  };

  // Bulk selection: voidable rows only (not paid, not void). Selection is
  // by _id; the action bar appears when anything's checked.
  const [selected, setSelected] = useState(new Set());
  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const isSelectable = (i) => i.status !== 'paid' && i.status !== 'void';

  const bulkSend = async () => {
    const ids = [...selected];
    let ok = 0, fail = 0;
    for (const id of ids) {
      const inv = invoices.find((i) => i._id === id);
      if (!inv || inv.status !== 'draft') continue;
      try { await invoicesService.send(facilityId, id); ok += 1; }
      catch { fail += 1; }
    }
    setSelected(new Set());
    fetchInvoices();
    toast[fail ? 'warning' : 'success'](`Sent ${ok} invoice(s)${fail ? `, ${fail} failed` : ''}.`);
  };

  const bulkVoid = async () => {
    const ids = [...selected];
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Void ${ids.length} selected invoice(s)? Paid invoices are skipped.`)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      const inv = invoices.find((i) => i._id === id);
      if (!inv || !isSelectable(inv)) continue;
      try { await invoicesService.void(facilityId, id); ok += 1; }
      catch { fail += 1; }
    }
    setSelected(new Set());
    fetchInvoices();
    toast[fail ? 'warning' : 'success'](`Voided ${ok} invoice(s)${fail ? `, ${fail} failed` : ''}.`);
  };

  const totalOutstanding = invoices
    .filter(i => i.status === 'sent' || i.status === 'overdue')
    .reduce((s, i) => s + (i.total || 0), 0);

  // Status filter + free-text search over tenant / unit / invoice #.
  const q = search.trim().toLowerCase();
  const filteredInvoices = invoices.filter((i) => {
    const isOpen = i.status !== 'paid' && i.status !== 'void';
    if (statusFilter === 'open' && !isOpen) return false;
    if (statusFilter === 'paid' && i.status !== 'paid') return false;
    if (statusFilter === 'void' && i.status !== 'void') return false;
    if (q) {
      const hay = `${i.invoice_number || ''} ${i.customer_name || ''} ${i.unit_number || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    const iso = String(i.due_date || '').slice(0, 10);
    if (fromDate && iso < fromDate) return false;
    if (toDate && iso > toDate) return false;
    if (!fromDate && !toDate && dateFilter !== 'all') {
      const now = new Date();
      const d = new Date(`${iso}T00:00:00`);
      if (Number.isNaN(d.getTime())) return false;
      if (dateFilter === 'today' && d.toDateString() !== now.toDateString()) return false;
      if (dateFilter === 'week' && (d < new Date(now.getTime() - 7 * 86400000) || d > new Date(now.getTime() + 86400000))) return false;
      if (dateFilter === 'month' && (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear())) return false;
    }
    return true;
  });

  // Every column sorts; default stays newest-due-date-first (the backend
  // sorts too, but the client filters over the result, so the display order
  // is guaranteed here). Blank dates sink to the bottom either way.
  const SORT_ACCESSORS = React.useMemo(() => ({
    invoice_number: (i) => i.invoice_number,
    tenant: (i) => `${i.customer_name || ''} ${i.unit_number || ''}`.trim(),
    due_date: (i) => i.due_date,
    amount: (i) => Number(i.amount || 0),
    tax: (i) => Number(i.tax_amount || 0),
    total: (i) => Number(i.total || 0),
    status: (i) => i.status,
    created_at: (i) => i.created_at,
    updated_at: (i) => i.updated_at,
  }), []);
  // Default: newest CREATED first (owner, 2026-08-05) — "what just got
  // minted" beats due-date order for the daily scan.
  const { applySort, sortKey, sortDir, toggle } = useTableSort(
    SORT_ACCESSORS, { key: 'created_at', dir: 'desc' });

  const pager = usePagedRows(applySort(filteredInvoices));

  const exportCsv = () => exportRowsToCsv(
    `invoices-${new Date().toISOString().slice(0, 10)}`,
    sortedInvoices,
    [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'customer_name', label: 'Customer' },
      { key: 'description', label: 'Description' },
      { key: 'due_date', label: 'Due' },
      { key: 'total', label: 'Total' },
      { key: 'amount_paid', label: 'Paid' },
      { key: 'status', label: 'Status' },
    ],
  );


  if (loading || facilityLoading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!facilityId) return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No facility selected</h2>
      <p className="text-[13px] text-slate-500 mt-1">Create a facility in Settings to manage invoices.</p>
    </div>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Invoices <HelpLink slug="invoices" /></h1>
          <p className="text-[13px] text-slate-400 mt-0.5">
            {statusFilter === 'all' && !q
              ? `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`
              : `${filteredInvoices.length} of ${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
        <button
          onClick={exportCsv}
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 font-medium text-white px-2.5 py-1.5 text-[12px] sm:px-4 sm:py-2 sm:text-[13px] hover:bg-blue-700 transition-colors"
        >
          Export CSV
        </button>
        <Link
          to="/invoices/new"
          className="flex items-center gap-1.5 rounded-lg bg-blue-600 text-white font-medium px-2.5 py-1.5 text-[12px] sm:px-4 sm:py-2 sm:text-[13px] hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" /> New Invoice
        </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl border border-slate-200/80 bg-white p-4">
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Total Invoices</p>
          <p className="text-xl font-semibold text-slate-900 mt-1">{invoices.length}</p>
        </div>
        <div className="rounded-xl border border-slate-200/80 bg-white p-4">
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Paid</p>
          <p className="text-xl font-semibold text-emerald-600 mt-1">{invoices.filter(i => i.status === 'paid').length}</p>
        </div>
        <div className="rounded-xl border border-slate-200/80 bg-white p-4">
          <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Outstanding</p>
          <p className="text-xl font-semibold text-red-600 mt-1">${totalOutstanding.toFixed(2)}</p>
        </div>
      </div>

      {/* Status filter + search */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4">
        {[
          { key: 'all', label: 'All' },
          { key: 'open', label: 'Open' },
          { key: 'paid', label: 'Paid' },
          { key: 'void', label: 'Void' },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              statusFilter === f.key
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
        <div className="flex-1" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tenant, unit, or invoice #..."
          className="w-64 max-w-full px-3 py-1.5 rounded-lg border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
        />
      </div>

      {/* Due-date window — pills + explicit range (range wins) */}
      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-4">
        {[
          { key: 'all', label: 'Any due date' },
          { key: 'today', label: 'Due today' },
          { key: 'week', label: 'Past 7 days' },
          { key: 'month', label: 'This month' },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => { setDateFilter(f.key); setFromDate(''); setToDate(''); }}
            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
              dateFilter === f.key && !fromDate && !toDate
                ? 'bg-blue-600 text-white'
                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="text-[12px] text-slate-400 ml-2">or range</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] text-slate-600" />
        <span className="text-[12px] text-slate-400">to</span>
        <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
          className="px-2 py-1.5 rounded-lg border border-slate-200 text-[12px] text-slate-600" />
        {(fromDate || toDate) && (
          <button type="button" onClick={() => { setFromDate(''); setToDate(''); }}
            className="text-[12px] text-blue-600 hover:underline">clear</button>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4"
             onClick={() => setDetail(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl p-5"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <div>
                <h2 className="text-[15px] font-semibold text-slate-900">
                  {detail.invoice_number || 'Invoice'}
                </h2>
                <p className="text-[12px] text-slate-500">
                  {detail.customer_name}{detail.unit_number ? ` · Unit ${detail.unit_number}` : ''}
                </p>
              </div>
              <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${STATUS_STYLES[detail.status] || 'bg-slate-50 text-slate-500'}`}>
                {detail.status}
              </span>
            </div>
            <p className="text-[12px] text-slate-600 mt-2">{detail.description}</p>
            <div className="mt-3 rounded-lg border border-slate-100 divide-y divide-slate-50 text-[13px]">
              <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">Charge</span><span className="text-slate-800">${(detail.amount ?? 0).toFixed(2)}</span></div>
              {(detail.discount_amount ?? 0) > 0 && (
                <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">Discount</span><span className="text-emerald-600">−${detail.discount_amount.toFixed(2)}</span></div>
              )}
              <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">Tax</span><span className="text-slate-800">${(detail.tax_amount ?? 0).toFixed(2)}</span></div>
              <div className="flex justify-between px-3 py-1.5 font-semibold"><span className="text-slate-700">Total</span><span className="text-slate-900">${(detail.total ?? 0).toFixed(2)}</span></div>
              <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">Paid</span><span className="text-slate-800">${(detail.amount_paid ?? 0).toFixed(2)}</span></div>
              <div className="flex justify-between px-3 py-1.5"><span className="text-slate-500">Due date</span><span className="text-slate-800">{formatDate(detail.due_date)}</span></div>
            </div>
            <div className="mt-3">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Payments applied</p>
              {detailPayments === null ? (
                <p className="text-[12px] text-slate-400">Loading…</p>
              ) : detailPayments.length === 0 ? (
                <p className="text-[12px] text-slate-400">
                  None recorded{detail.status === 'paid' ? ' (paid before payment-link tracking began)' : ''}.
                </p>
              ) : (
                <div className="rounded-lg border border-slate-100 divide-y divide-slate-50">
                  {detailPayments.map((pm) => (
                    <div key={pm.id} className="flex justify-between px-3 py-1.5 text-[12px]">
                      <span className="text-slate-600">{formatDate(pm.payment_date)} · {pm.payment_method}</span>
                      <span className="text-slate-800 font-medium">${(pm.amount ?? 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setDetail(null)}
                className="px-4 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        {selected.size > 0 && (
          <div className="flex items-center justify-between px-4 py-2.5 mb-2 rounded-lg border border-blue-200/80 bg-blue-50/60">
            <span className="text-[13px] font-medium text-slate-700">{selected.size} selected</span>
            <div className="flex items-center gap-2">
              <button onClick={bulkSend}
                      className="rounded-lg bg-blue-600 text-white text-[12px] font-medium px-3.5 py-1.5 hover:bg-blue-700">
                Send drafts
              </button>
              <button onClick={bulkVoid}
                      className="rounded-lg bg-red-600 text-white text-[12px] font-medium px-3.5 py-1.5 hover:bg-red-700">
                Void selected
              </button>
              <button onClick={() => setSelected(new Set())}
                      className="text-[12px] text-slate-500 hover:text-slate-700 font-medium">
                Clear
              </button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-100">
              <th className="px-4 py-2.5 w-8">
                <span className="sr-only">Select</span>
              </th>
              {[
                { k: 'invoice_number', label: 'Invoice #' },
                { k: 'tenant', label: 'Tenant · Unit' },
                { k: 'due_date', label: 'Due Date' },
                { k: 'amount', label: 'Amount', align: 'right' },
                { k: 'tax', label: 'Tax', align: 'right' },
                { k: 'total', label: 'Total', align: 'right' },
                { k: 'status', label: 'Status' },
                { k: 'created_at', label: 'Created' },
                { k: 'updated_at', label: 'Updated' },
              ].map(({ k, label, align }) => (
                <SortTh key={k} label={label} sortKey={k} align={align}
                  current={sortKey} dir={sortDir} onSort={toggle} />
              ))}
              <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {pager.pageRows.map((inv) => (
              <tr key={inv._id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-4 py-3 w-8">
                  {isSelectable(inv) && (
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={selected.has(inv._id)}
                      onChange={() => toggleSel(inv._id)}
                    />
                  )}
                </td>
                <td className="px-4 py-3 text-[13px] font-medium">
                  <button type="button" onClick={() => openDetail(inv)}
                    className="text-blue-600 hover:underline"
                    title="View breakdown">
                    {inv.invoice_number || 'view'}
                  </button>
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-700">
                  {inv.customer_name || <span className="text-slate-400">—</span>}
                  {inv.unit_number && <span className="text-slate-400"> · Unit {inv.unit_number}</span>}
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-500">
                  <span className="flex items-center gap-1.5"><Calendar className="h-3 w-3 text-slate-300" />{formatDate(inv.due_date)}</span>
                </td>
                <td className="px-4 py-3 text-[13px] text-slate-500 text-right">${inv.amount?.toFixed(2)}</td>
                <td className="px-4 py-3 text-[13px] text-slate-500 text-right">${inv.tax_amount?.toFixed(2)}</td>
                <td className="px-4 py-3 text-[13px] font-semibold text-slate-700 text-right">${inv.total?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${STATUS_STYLES[inv.status] || 'bg-slate-50 text-slate-500'}`}>
                    {inv.status}
                  </span>
                </td>
                {/* Stored naive-UTC; shown on the FACILITY clock. */}
                <td className="px-4 py-3 text-[12px] text-slate-400 whitespace-nowrap">
                  {inv.created_at ? formatDateTime(inv.created_at, currentFacility?.timezone) : '—'}
                </td>
                <td className="px-4 py-3 text-[12px] text-slate-400 whitespace-nowrap">
                  {inv.updated_at ? formatDateTime(inv.updated_at, currentFacility?.timezone) : '—'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {(inv.status === 'draft' || inv.status === 'sent') && !inv.stripe_invoice_id && (
                      <Link to={`/invoices/${inv._id}/edit`} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Link>
                    )}
                    {inv.status === 'draft' && (
                      <button onClick={() => handleSend(inv._id)} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Send">
                        <Send className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {inv.status !== 'void' && inv.status !== 'paid' && (
                      <button onClick={() => handleVoid(inv._id)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Void">
                        <XCircle className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr><td colSpan="11" className="px-4 py-8 text-center text-[13px] text-slate-400">{invoices.length === 0 ? 'No invoices yet' : 'No invoices match this filter'}</td></tr>
            )}
          </tbody>
        </table>
        </div>
        <ListPager {...pager} noun="invoices" />
      </div>
    </div>
  );
};

export default Invoices;
